import { useMutation } from '@tanstack/react-query';
import { Button, Dialog, DialogActions, Notice, printSheet } from '@wodi/ui';
import { useRef, useState } from 'react';

import { recordLabReportPrinted } from '../api/lab.ts';
import { errorText } from '../stock/common.tsx';
import styles from './lab.module.css';
import { LabReportSheet, type ReportPrintData } from './prints.tsx';

/**
 * A preview of the lab report and "Print". Each print is recorded, with its copy number,
 * as the print of a clinical document (plan §8.6).
 */
export function PrintReportDialog({
  data,
  onClose,
}: {
  data: Omit<ReportPrintData, 'copy'>;
  onClose: () => void;
}) {
  const sheet = useRef<HTMLElement>(null);
  const [copy, setCopy] = useState(1);
  const recordPrint = useMutation({
    mutationFn: (n: number) => recordLabReportPrinted(data.report.id, n),
    onSuccess: (_d, n) => {
      setCopy(n + 1);
    },
  });
  return (
    <Dialog
      title="Print report"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <div className={styles.preview}>
        <LabReportSheet sheetRef={sheet} data={{ ...data, copy }} />
      </div>
      {recordPrint.isError ? (
        <Notice tone="error">
          {errorText(recordPrint.error, "The print wasn't recorded. Print again to record it.")}
        </Notice>
      ) : null}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Close
        </Button>
        <Button
          variant="primary"
          onPress={() => {
            if (sheet.current === null) return;
            printSheet(sheet.current);
            recordPrint.mutate(copy);
          }}
        >
          Print
        </Button>
      </DialogActions>
    </Dialog>
  );
}
